/**
 * `AppWiring` — the one wiring of a Lona app client, shared by every host:
 * a browser host and the headless CLI build the same client, seed the same
 * session, open sheets the same two ways and pick the same column renderer,
 * differing only in the adapters a `Host` names.
 *
 * What stays in a host (and is passed in through `Host` or a view input):
 * the transport and credentials, preference storage, the renderer host, the
 * durable-write executor, the appearance reads, the promote callback, the
 * request-error policy, the registry overrides (render-flag controls), the
 * invalidation sink, the rows backend, the recompute scheduler, the clock and a timezone pin, and —
 * per view — the debug rows, the descriptor overrides and the renderer
 * wrapper (a DOM binder, or none). Nothing here reaches the DOM or
 * `@tento/lona/sheets-ui`.
 *
 * @module
 */

import {
  Actor,
  type Backend,
  type CalendarConfigProvider,
  InvalidationSink,
  isBuiltinSheetKey,
  type Option,
  type RecomputeScheduler,
  SheetRegistries,
  type UserId,
} from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { ColumnIndex } from "@tento-core/column-index";
import { SheetLayoutWidth } from "@tento-core/sheet-layout/sheet-layout-width";
import { Tracing } from "@tento-core/tracing";
import { NaiveDate, type PartialDate, TimezoneRegion, Tzname, type Weekday } from "@tento-chrono";

import { type ClientConfig, LonaSdk, platformOf, type SheetHandle, type SheetPromoted } from "./client.ts";
import type { DebounceExecutor, ListenerErrorHandler, RequestErrorHandler } from "./client-seams.ts";
import type { Appearance } from "../events/event-category-item.ts";
import { FlightInfo } from "../timeline/flight-info.ts";
import { FlightMapper } from "../timeline/flight-mapper.ts";
import { IndexMapperTimeTraveler } from "../timeline/index-mapper.ts";
import type { Network } from "../network.ts";
import type { PreferencesStorage } from "../preferences/preferences-storage.ts";
import { ZoneLoad } from "../preferences/zone-load.ts";
import type { RowTypeDescriptor } from "../rows/row-descriptor.ts";
import type { SheetHost } from "./sheet-host.ts";
import type { SheetViewConfig } from "@tento-lona/sheets";
import { SheetViewModel } from "../view-model/sheet-view-model.ts";

/**
 * The app client's wiring: `clientConfig(host)` builds it, `bootSession` /
 * `fetchSession` bind its session, `openInitial` / `navigationHandle` open a
 * sheet the two ways the app does, and `viewModelDeps` makes the renderer
 * factory every `SheetViewModel` of the app is made with.
 */
export namespace AppWiring {
  /**
   * The host-specific adapters of an app client. Every field is required:
   * the wiring has no defaults, so two hosts differ exactly in what they
   * pass here.
   */
  export interface Host {
    /** API origin; `null` for same-origin requests (the browser app). */
    readonly baseUrl: Option<string>;
    /** Bearer token; `null` when the host authenticates by cookie or runs offline. */
    readonly accessToken: Option<string>;
    /** The client's transport; `null` uses the global `fetch`. */
    readonly fetch: Option<typeof fetch>;
    /** Preference storage (`localStorage`, or a `MemoryStorage` snapshot). */
    readonly storage: PreferencesStorage;
    /** The renderer host (`DOM_HOST`, `SheetHost.HEADLESS`). */
    readonly sheetHost: SheetHost;
    /** Who runs durable writes (`DebounceExecutors.browser(bridge)` / `.inProcess()`). */
    readonly debounceExecutor: DebounceExecutor;
    /** How event categories look (`Appearance.HEADLESS` for a headless host). */
    readonly appearance: Appearance;
    /** The promote callback (a browser host navigates to the promoted sheet), or `null`. */
    readonly onSheetPromoted: Option<(event: SheetPromoted) => void>;
    /** Where swallowed tolerant-read failures go. */
    readonly requestErrorHandler: RequestErrorHandler;
    /** Where what a subscriber threw goes (`ClientConfig.listenerErrorHandler`); absent: `ListenerErrorHandlers.RETHROW`. */
    readonly listenerErrorHandler?: ListenerErrorHandler;
    /**
     * The frame-budget port for computed-row recomputes (`ClientConfig.recomputeScheduler`):
     * a browser host sets it from its frame deadline (`browserHost`); absent: all of a recompute runs now.
     */
    readonly recomputeScheduler?: RecomputeScheduler;
    /** Layered over `SheetRegistries.BUILTINS` (a browser host's render-flag controls); `null`: the built-ins. */
    readonly registryOverrides: Option<SheetRegistries.Overrides>;
    /** The client's invalidation sink, made once per client. */
    readonly invalidation: () => InvalidationSink.Live;
    /** The rows-side backend, made once per client during construction. */
    readonly backend: (client: LonaSdk.Client) => Backend;
    /**
     * Today for a fresh view — its column-0 anchor (`anchorToday`). `zone`
     * is the effective preferred zone, `browserZone` the zone the host's own
     * clock reads (`browserTimezone`). A browser anchors on its own clock
     * (`NaiveDate.today()`: the day in `browserZone`); a headless host on a
     * pinned day, or the day in `browserZone`.
     */
    readonly today: (zone: TimezoneRegion, browserZone: TimezoneRegion) => NaiveDate;
    /**
     * An IANA zone pinned into the preferences before the first open
     * (`resolveTime`); `null` keeps the stored preference, else the host's zone.
     */
    readonly timezone: Option<string>;
    /**
     * The zone the host's clock reads — a browser's own zone
     * (`Intl.DateTimeFormat().resolvedOptions().timeZone`), which may differ
     * from the preferred zone; `null`: the effective preferred zone.
     */
    readonly browserTimezone: Option<string>;
  }

  /** The client config of `host` (`new LonaSdk.Client(AppWiring.clientConfig(host))`). */
  export function clientConfig(host: Host): ClientConfig {
    return {
      storage: host.storage,
      host: host.sheetHost,
      registries: host.registryOverrides == null
        ? SheetRegistries.BUILTINS
        : SheetRegistries.extend(SheetRegistries.BUILTINS, host.registryOverrides),
      onSheetPromoted: host.onSheetPromoted,
      invalidation: host.invalidation(),
      baseUrl: host.baseUrl,
      accessToken: host.accessToken,
      fetch: host.fetch,
      rowsOptions: (client) => rowsOptions(client, host.backend(client)),
      requestErrorHandler: host.requestErrorHandler,
      ...(host.listenerErrorHandler != null ? { listenerErrorHandler: host.listenerErrorHandler } : {}),
      ...(host.recomputeScheduler != null ? { recomputeScheduler: host.recomputeScheduler } : {}),
      debounceExecutor: host.debounceExecutor,
      appearance: host.appearance,
    };
  }

  /**
   * The rows-side wiring every host gets, over `backend`: the `calendar-list` provider
   * over `client.calendars` (`calendarConfig`), no task lists, and the
   * client's calendar preferences for the preference aliases
   * (`alias.secondary-timezones`, `alias.default-weather`).
   */
  export function rowsOptions(
    client: LonaSdk.Client,
    backend: Backend,
  ): LonaSdk.Client.RowsOptions {
    return {
      backend,
      calendarConfig: calendarConfig(client),
      taskListConfig: LonaSdk.Client.EMPTY_TASK_LIST_CONFIG,
      calendarPrefs: client.preferences.calendar(),
    };
  }

  /**
   * The `calendar-list` alias's provider: the calendars `client.calendars`
   * holds, read at expansion time — none until a session seeded or loaded
   * them (a template can expand before the boot payload arrives).
   */
  export function calendarConfig(client: LonaSdk.Client): CalendarConfigProvider {
    return {
      calendars: () =>
        (client.calendars.isLoaded() ? client.calendars.peekAll() : []).flatMap((list) =>
          list.calendars.map((calendar) => {
            const summary = calendar.summary;
            return {
              authId: list.authId,
              calendarId: calendar.id,
              label: typeof summary === "string" && summary.length > 0 ? summary : calendar.id,
              // Server-rendered key — required for non-google calendars;
              // without it the alias factory's fallback builds a
              // `:~google:` key for outlook rows.
              eventsLookupKey: calendar.eventsLookupKey,
              source: calendar.source,
            };
          })
        ),
    };
  }

  /** A client's bound session: its user, and how it was bound. */
  export interface Session {
    /** The signed-in user (`client.setCurrentUser`). */
    readonly user: UserId;
    /** `"boot"`: seeded from a boot payload (`bootSession`); `"network"`: fetched (`fetchSession`). */
    readonly via: "boot" | "network";
  }

  /** What a boot payload seeds (`Network.InitialPayload`'s user, calendars and sheet list). */
  export type Boot = LonaSdk.Client.Boot;

  /** The `Boot` of a boot payload (`Network.InitialPayload`), calendars as given. */
  export function bootOf(payload: Network.InitialPayload): Boot {
    return { user: payload.user.id, calendars: payload.calendars, sheets: payload.sheetList };
  }

  /**
   * Bind the session a boot payload carries: the signed-in user
   * (`setCurrentUser`, which scopes `client.sheetKeys`), then its calendars
   * and sheet list into the client's caches (`platformOf(client).seedBoot`).
   * Sends nothing.
   */
  export function bootSession(client: LonaSdk.Client, boot: Boot): Session {
    client.setCurrentUser(boot.user);
    platformOf(client).seedBoot(boot);
    return { user: boot.user, via: "boot" };
  }

  /**
   * Bind the session over the network, for a host with no boot payload:
   * `GET /api/user`, `setCurrentUser`, then the user's calendars
   * (`client.calendars.ensureLoaded`). The sheet list is not loaded (a boot
   * payload seeds it; `client.sheets` loads it on first use).
   */
  export async function fetchSession(client: LonaSdk.Client): Promise<Session> {
    const user = nonnull(
      await client.user.get(),
      "AppWiring.fetchSession: GET /api/user returned no user (check the access token)",
    );
    client.setCurrentUser(user.id);
    await client.calendars.ensureLoaded(user.id);
    return { user: user.id, via: "network" };
  }

  /** A zone by IANA name, a backward link included (`zoneOpt`); throws `what` when it is unknown. */
  async function zoneNamed(name: string, what: string): Promise<TimezoneRegion> {
    return nonnull(await zoneOpt(name), what);
  }

  /**
   * A zone by IANA name, or `null` when it is unknown, through the SDK's zone
   * loader (`ZoneLoad.opt`): a backward link (`US/Pacific`, `US/Eastern`)
   * loads as its canonical zone (`America/Los_Angeles`), the stored name
   * passed as it is.
   */
  async function zoneOpt(name: string): Promise<Option<TimezoneRegion>> {
    return await ZoneLoad.opt(name);
  }

  /** The zone, today and week start a fresh view anchors on (`anchorToday`). */
  export interface Time {
    /** The effective preferred zone. */
    readonly zone: TimezoneRegion;
    /** The zone the host's clock reads (`Host.browserTimezone`, else `zone`). */
    readonly browserZone: TimezoneRegion;
    /** `Host.today(zone, browserZone)`: column 0's day. */
    readonly today: NaiveDate;
    /** The first day of a week column (`lona-pref-first-day`). */
    readonly weekStart: Weekday;
  }

  /**
   * The one answer to "which day is today for this view": `host.today` in
   * the effective preferred zone and the host's clock zone, with the week
   * start. Every fresh view anchors here — a browser host's boot payload and
   * navigation opens, and the headless CLI's (`resolveTime`). The
   * effective zone is `loadEffectiveRegion`'s: a stored or runtime zone that
   * does not load falls back (the runtime's zone, then UTC), traced
   * (`tz/zone-fallback`), and never rejects the view (V8 reports an India
   * runtime as the legacy `Asia/Calcutta`). Throws on an unknown
   * `host.browserTimezone`; `resolveTime` throws on an unknown pinned zone.
   */
  export async function anchorToday(
    client: LonaSdk.Client,
    host: Pick<Host, "today" | "browserTimezone">,
  ): Promise<Time> {
    const zone = await client.preferences.timezone().loadEffectiveRegion();
    const browserZone = host.browserTimezone == null
      ? zone
      : await zoneNamed(
        host.browserTimezone,
        `AppWiring: browser timezone "${host.browserTimezone}" is not a known IANA timezone`,
      );
    return { zone, browserZone, today: host.today(zone, browserZone), weekStart: client.preferences.calendar().startOfWeek() };
  }

  /**
   * Pin `host.timezone` into the client's preferences (when set), then
   * resolve the effective zone and the anchor day (`anchorToday`). Throws
   * when the pinned `host.timezone` does not load (an explicit choice is
   * never replaced by a fallback): as unknown when the runtime knows no zone
   * by that name, as not loading when it does.
   */
  export async function resolveTime(
    client: LonaSdk.Client,
    host: Pick<Host, "timezone" | "today" | "browserTimezone">,
  ): Promise<Time> {
    const prefs = client.preferences;
    if (host.timezone != null) {
      const tz = nonnull(Tzname.parseOpt(host.timezone), `AppWiring: timezone "${host.timezone}" is not a known IANA timezone`);
      nonnull(await zoneOpt(host.timezone), `AppWiring: timezone "${host.timezone}" did not load`);
      prefs.timezone().set(tz, Actor.EXTERNAL);
    }
    return await anchorToday(client, host);
  }

  /**
   * The handle a navigation to `key` opens: a built-in catalog name through
   * `openBuiltin` (the user's copy, else the seeded catalog sheet), any
   * other key as a typed `SheetKey` (`client.sheetKeys`), which throws
   * `SheetNotFoundError` on a miss.
   */
  export function navigationHandle(client: LonaSdk.Client, key: string): Promise<SheetHandle> {
    return isBuiltinSheetKey(key) ? client.openBuiltin(key) : client.open(client.sheetKeys.make(key, null));
  }

  /** A navigation's view: `navigationHandle`, then `SheetViewModel.make` anchored on `today`. */
  export async function openByNavigation<Binder>(
    key: string,
    deps: SheetViewModel.Deps<Binder>,
    today: NaiveDate,
  ): Promise<SheetViewModel<Binder>> {
    const handle = await navigationHandle(deps.sdk, key);
    try {
      return await SheetViewModel.make(handle, deps, today, null);
    } catch (error) {
      try { handle.dispose(); } catch { /* Preserve the preparation failure. */ }
      throw error;
    }
  }

  /**
   * The boot payload's own sheet as the first view: `SheetViewModel.make`
   * parses the payload sheet (no `open`, nothing sent for the sheet itself).
   */
  export function openInitial<Binder>(
    payloadSheet: Network.UserSheetWithRows,
    deps: SheetViewModel.Deps<Binder>,
    today: NaiveDate,
  ): Promise<SheetViewModel<Binder>> {
    return SheetViewModel.make(payloadSheet, deps, today, null);
  }

  /** The column-index mapper a view renders with. */
  export type Mapper = ColumnIndex.Mapper<FlightInfo<Network.EventCell>>;

  /**
   * The host-neutral half of a view's renderer, from which a host builds its
   * `SheetViewModel.Renderer` (`ViewInputs.renderer`).
   */
  export interface RendererParts {
    /** The time-traveler flight mapper for a `day` + `time-traveler` view, else the default mapper. */
    readonly mapper: Mapper;
    /** Column 0 (the period containing the anchor date). */
    readonly anchor: PartialDate;
    /** The preferred zone the columns' boundaries are in. */
    readonly zone: TimezoneRegion;
    /** The first day of a week column. */
    readonly weekStart: Weekday;
    /** Whether `mapper` is the time-traveler flight mapper. */
    readonly timeTraveler: boolean;
    /** The view's width: its column count. */
    readonly initialWidth: SheetLayoutWidth;
    /** The column constraints (`constraintsForView`). */
    readonly initialConstraints: SheetLayoutWidth.Constraints;
    /** A time-traveler in-flight column's fixed width (400), else `null`. */
    widthForIndex(index: number): Option<number>;
  }

  /** What a host adds to the shared renderer choice, per client. */
  export interface ViewInputs<Binder> {
    /** The debug system rows (`[]`: none). */
    readonly debugRows: readonly SheetHandle.DebugRow[];
    /** Descriptor overrides the host's render plugins supply (`[]`: none). */
    readonly descriptorOverrides: Iterable<[string, RowTypeDescriptor.Override]>;
    /** Apply the stored row heights (`lona-pref-row-heights`) to the row registry first. */
    readonly applyStoredRowHeights: boolean;
    /** The host's renderer over the shared parts (a DOM binder wrapper, or a headless one). */
    readonly renderer: (parts: RendererParts) => SheetViewModel.Renderer<Binder>;
  }

  /**
   * The view-model deps of `client`: registers the stored row heights and
   * the descriptor overrides once (a browser host's boot), and returns the
   * deps whose renderer picks the mapper (`rendererParts`) and hands the
   * parts to `inputs.renderer`.
   */
  export function viewModelDeps<Binder>(
    client: LonaSdk.Client,
    inputs: ViewInputs<Binder>,
  ): SheetViewModel.Deps<Binder> {
    if (inputs.applyStoredRowHeights) platformOf(client).applyStoredRowHeights();
    platformOf(client).registerDescriptorOverrides(inputs.descriptorOverrides);
    return {
      sdk: client,
      debugRows: inputs.debugRows,
      renderer: async (anchorDate, viewConfig, preferredColumnWidthPx, columnCount) =>
        inputs.renderer(
          await rendererParts(client, anchorDate, viewConfig, preferredColumnWidthPx, columnCount),
        ),
    };
  }

  /** A view's column constraints: `derived` counts are weak; day columns are narrower. */
  export function constraintsForView(
    viewConfig: SheetViewConfig,
    preferredColumnWidthPx: Option<number>,
    columnCount: SheetViewModel.ColumnCount,
  ): SheetLayoutWidth.Constraints {
    const isDay = viewConfig.timescale === "day";
    return {
      weak: columnCount === "derived",
      preferredWidthPx: preferredColumnWidthPx ?? (isDay ? 40 : 240),
      minWidthPx: isDay ? 20 : 120,
    };
  }

  /**
   * The renderer choice every host shares: in the preferred zone, a
   * `day` + `time-traveler` view maps through a `FlightMapper` over the
   * user's timezone-transition events (`events.listTzTransitions`, a
   * request); any other view through `ColumnIndex.Mapper.Default` anchored
   * on the period of `anchorDate`.
   */
  export async function rendererParts(
    client: LonaSdk.Client,
    anchorDate: NaiveDate,
    viewConfig: SheetViewConfig,
    preferredColumnWidthPx: Option<number>,
    columnCount: SheetViewModel.ColumnCount,
  ): Promise<RendererParts> {
    const weekStart = client.preferences.calendar().startOfWeek();
    const preferredTimezone = client.preferences.timezone().effectiveTimezoneName();
    const zone = await client.preferences.timezone().loadEffectiveRegion();
    const initialWidth = SheetLayoutWidth.fromCount(viewConfig.count);
    const initialConstraints = constraintsForView(viewConfig, preferredColumnWidthPx, columnCount);

    Tracing.info("ui")("sheet/renderer/make", {
      ...viewConfig,
      preferredColumnWidthPx,
      columnCount,
    });
    if (viewConfig.timescale === "day" && viewConfig.tzType === "time-traveler") {
      const anchor = NaiveDate.Partial.fromDateAndType("day", anchorDate);
      const tzStart = performance.now();
      const tzs = await client.events.listTzTransitions();
      const tzDuration = performance.now() - tzStart;
      if (tzDuration > 100) {
        Tracing.warn("ui")(`renderer/getTzTransitions SLOW: ${tzDuration}ms`);
      }
      Tracing.log("renderer-getTzTransitions", {
        count: tzs.length,
        eventIds: tzs.slice(0, 5).map((t) => t.id),
      });
      // Skip the FlightInfo pass when there are no timezone events.
      const flightinfos: FlightInfo<Network.EventCell>[] = tzs.length > 0
        ? await FlightInfo.fromEvents(tzs, preferredTimezone)
        : [];
      if (flightinfos.length === 0 && tzs.length > 0) {
        Tracing.warn("ui")(
          "sheet/renderer/time-traveler: No flight infos created despite having timezone events",
          { tzEventsCount: tzs.length, preferredTimezone },
        );
      } else if (flightinfos.length === 0) {
        Tracing.info("ui")(
          "sheet/renderer/time-traveler: No timezone events found - flight cards will not render",
        );
      }
      const mapper = new IndexMapperTimeTraveler(
        new FlightMapper(anchor.start, flightinfos, zone),
        anchor,
        zone,
      );
      return {
        mapper,
        anchor,
        zone,
        weekStart,
        timeTraveler: true,
        initialWidth,
        initialConstraints,
        widthForIndex: (index) => (mapper.isInFlightIndex(index) ? 400 : null),
      };
    }

    // The first parameter IS the anchor date: `goToDate` / ephemeral
    // anchoring move the window by passing another one.
    const anchor = NaiveDate.Partial.fromDateAndType(viewConfig.timescale, anchorDate, weekStart);
    return {
      mapper: new ColumnIndex.Mapper.Default<FlightInfo<Network.EventCell>>(anchor, zone),
      anchor,
      zone,
      weekStart,
      timeTraveler: false,
      initialWidth,
      initialConstraints,
      widthForIndex: () => null,
    };
  }
}
